import { MonoBehavior } from 'game/core/monoBehavior';
import { Container, Graphics, Point } from 'pixi.js';
import SettingsButton from './SettingsButton';
import { MENU_TRANSITION_TIME } from 'constants/move';
import { Layout } from 'game/LayoutManager';
import { BUTTONS_BOTTOM_MARGIN_DESKTOP, BUTTONS_PADDING, DESKTOP_SIDEBAR_WIDTH, Displays } from 'constants/layout';
import gsap, { Power1 } from 'gsap';
import { getScreenSize } from 'game/core/screen';
import { GameUI } from './GameUI';
import { addToUI } from 'game/core/Playable2DRootContainer';
import { CircleButton } from './circleButton';
import { showShareModal } from 'game/shareModal';
import { Store } from 'game/store/gameStore';
import { GameEvent } from 'game/events/gameEvent';
import { EventName } from 'game/events/eventsName';
import { showPopup } from 'game/showPopup';
import { GAEvent, GAEventNames } from 'game/analytics/events';
import { GameName } from 'constants/difficulty';
import { isIOS } from 'game/core/display';
import { translate } from 'game/translate';

export class MenuButtons extends MonoBehavior {
    settingsButton: SettingsButton;
    designButton: CircleButton;
    rulesButton: CircleButton;
    shareButton: CircleButton;
    menuButton: CircleButton;
    closeMenuButton: CircleButton;
    isMenuVisible = false;
    overlay: Container;
    overlayRect: Graphics;
    gameUI: GameUI;
    buttonContainer: Container;
    buttons = [] as CircleButton[];

    constructor(parent: GameUI) {
        super();
        this.gameUI = parent;
        this.showMobileMenu = this.showMobileMenu.bind(this);
        this.hideMobileMenu = this.hideMobileMenu.bind(this);
        this.hideOptions = this.hideOptions.bind(this);
        this.showDesign = this.showDesign.bind(this);
        this.rulesClick = this.rulesClick.bind(this);
        this.showSettings = this.showSettings.bind(this);

        this.settingsButton = new SettingsButton();
        this.designButton = new CircleButton('design', true);
        this.rulesButton = new CircleButton('rules', true);
        this.shareButton = new CircleButton('share', true);
        this.menuButton = new CircleButton('dots', true);
        this.closeMenuButton = new CircleButton('close', true);
        this.buttons.push(this.settingsButton, this.designButton, this.rulesButton, this.shareButton);

        this.settingsButton.onClick(this.showSettings);
        this.designButton.onClick(this.showDesign);
        this.rulesButton.onClick(this.rulesClick);
        this.menuButton.onClick(this.showMobileMenu);
        this.shareButton.onClick(showShareModal);
        this.closeMenuButton.onClick(this.hideMobileMenu);
        this.buttonContainer = new Container();
        this.overlay = new Container();
        this.overlayRect = new Graphics();
        this.createOverlay();
        this.buttonContainer.zIndex = 1000;
        addToUI(this.buttonContainer);
        GameEvent.add(EventName.CLOSE_SETTINGS, this.hideOptions.bind(this));
        GameEvent.add(EventName.START_TUTORIAL, this.onTutorialChange.bind(this));
        GameEvent.add(EventName.STOP_TUTORIAL, this.onTutorialChange.bind(this));
    }

    start(): void {
        this.buttonContainer.addChild(this.settingsButton.container);
        this.buttonContainer.addChild(this.designButton.container);
        this.buttonContainer.addChild(this.rulesButton.container);
        this.buttonContainer.addChild(this.shareButton.container);
        this.buttonContainer.addChild(this.menuButton.container);
        this.buttonContainer.addChild(this.closeMenuButton.container);
    }
    showDesign() {
        this.hideButtons();
        this.overlay.eventMode = 'static';
        this.gameUI.showDesign();
    }
    showSettings() {
        this.hideButtons();
        this.overlay.eventMode = 'static';
        this.gameUI.showSettings();
    }
    createOverlay() {
        this.overlayRect = new Graphics();
        this.overlay.addChild(this.overlayRect);
        this.overlay.eventMode = 'none';
        this.overlay.on('pointerdown', this.hideOptions);
        addToUI(this.overlay);
    }
    handleOptions(onResize = false, areOptionsVisible = false) {
        const { width, height } = getScreenSize();
        const device = Layout.getDisplay();

        this.overlayRect.clear();
        this.overlayRect.rect(0, 0, width, height);
        this.overlayRect.fill(0x000000);

        this.overlayRect.alpha = 0.00001;
        this.overlay.position.set(0, 0);

        const buttonPadding = Layout.getButtonPadding();

        const startPosition = new Point(
            buttonPadding.x + Layout.getButtonSize() / 2,
            height - (buttonPadding.y + Layout.getButtonSize() / 2)
        );

        switch (device) {
            case Displays.DESKTOP:
                this.isMenuVisible = true;
                this.setMenuVisible(onResize, areOptionsVisible);
                this.menuButton.setDisabled(true);
                this.closeMenuButton.setDisabled(true);
                this.menuButton.container.alpha = 0;
                this.closeMenuButton.container.alpha = 0;

                const center = width - DESKTOP_SIDEBAR_WIDTH * 0.5;
                const buttonHeight = Layout.getButtonSize();
                this.buttons.forEach((button, index) => {
                    button.container.position.set(
                        center,
                        height - buttonHeight * (index + 0.5) - BUTTONS_BOTTOM_MARGIN_DESKTOP - BUTTONS_PADDING * index
                    );
                });

                this.isMenuVisible = true;

                break;

            case Displays.TABLET:
                const tabletMargin = 70;
                this.isMenuVisible = true;
                this.setMenuVisible(onResize, areOptionsVisible);

                this.buttons.forEach((button, index) => {
                    button.container.position.set(startPosition.x + tabletMargin * index, startPosition.y);
                });

                this.isMenuVisible = true;

                break;
            case Displays.MOBILE:
                this.isMenuVisible = false;
                this.setMenuVisible(onResize, areOptionsVisible);

                const mobileMargin = 56;
                this.menuButton.container.position.set(startPosition.x, startPosition.y);

                this.buttons.forEach((button, index) => {
                    button.container.position.set(startPosition.x, startPosition.y - mobileMargin * (1 + index));
                });
                this.closeMenuButton.container.position.set(startPosition.x, startPosition.y);

                break;
        }
    }
    setMenuVisible(force = false, areOptionsVisible = false) {
        const easeTime = force ? 0 : MENU_TRANSITION_TIME;
        if (this.isMenuVisible) {
            this.menuButton.setDisabled(true);

            this.closeMenuButton.container.alpha = 1;
            this.menuButton.container.alpha = 0;
            this.buttons.forEach((button, index) => {
                button.setDisabled(areOptionsVisible);
            });

            this.closeMenuButton.setDisabled(areOptionsVisible);
            this.buttons.forEach((button, index) => {
                gsap.to(button.container, { alpha: 1, ease: Power1.easeInOut, duration: easeTime });
            });
        } else {
            this.closeMenuButton.setDisabled(true);
            this.closeMenuButton.container.alpha = 0;
            this.menuButton.container.alpha = 1;
            this.buttons.forEach((button, index) => {
                button.setDisabled(true);
            });
            this.menuButton.setDisabled(areOptionsVisible);
            this.buttons.forEach((button, index) => {
                gsap.to(button.container, { alpha: 0, ease: Power1.easeInOut, duration: easeTime });
            });
        }
        if (Layout.getDisplay() !== Displays.MOBILE) {
            this.closeMenuButton.container.alpha = 0;
            this.closeMenuButton.setDisabled(true);
        }
        this.checkMobileMenuNotification();
    }

    showMobileMenu() {
        this.isMenuVisible = true;
        this.setMenuVisible();
        this.overlay.eventMode = 'static';
    }
    checkMobileMenuNotification() {
        if (Store.notificationIcons.length) {
            this.menuButton.addNotification();
            this.closeMenuButton.addNotification();
        } else {
            this.menuButton.removeNotification();
            this.closeMenuButton.removeNotification();
        }
    }
    hideMobileMenu() {
        if (Layout.getDisplay() === Displays.MOBILE) {
            this.isMenuVisible = false;
            this.setMenuVisible(false);
        }
        this.overlay.eventMode = 'none';
    }
    hideOptions() {
        this.overlay.eventMode = 'none';
        this.gameUI.hideOptions();
        this.hideMobileMenu();
    }
    hideButtons() {
        gsap.to(this.buttonContainer, { alpha: 0, ease: Power1.easeInOut, duration: MENU_TRANSITION_TIME });
    }
    showButtons() {
        gsap.to(this.buttonContainer, { alpha: 1, ease: Power1.easeInOut, duration: MENU_TRANSITION_TIME });
    }
    rulesClick() {
        if (!Store.availableGamesToTutorial.includes(Store.name)) {
            GAEvent(GAEventNames.GAME_RULES_OPEN, {
                timeSec: Store.time,
            });
            let url = '';
            switch (Store.name) {
                case GameName.KLONDIKE:
                    url = translate('rulesKlondikeURL');
                    break;
                case GameName.SPIDER:
                    url = translate('rulesSpiderURL');
                    break;
                case GameName.FREECELL:
                    url = translate('rulesFreecellURL');
                    break;
                case GameName.PYRAMID:
                    url = translate('rulesPyramidURL');
                break;
            }
    
            if (isIOS()) window.location.href = url;
            else window.open(url, '_blank');
            
            return;
        }

        showPopup('tutorial_start');

        GAEvent(GAEventNames.TUTORIAL_SHOW_BANNER, {
            number: Store.countPopupShown('tutorial_start'),
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
        });

        if (import.meta.url.includes('localhost:5173')) {
            console.warn("%cREMOVE THIS LINE - it's for testing purposes only", 'color: red; font-weight: bold;');
            dispatchEvent(new CustomEvent('closeModal', { detail: { id: 'tutorial_start', confirm: true } }));
        }
    }
    onTutorialChange() {
        this.handleOptions();

        if (Store.tutorialActive) {
            this.setMenuVisible(false);
            this.hideMobileMenu();
            this.hideButtons();
            this.buttons.forEach((button) => {
                button.setDisabled(true);
            });
        } else {
            this.setMenuVisible(true);
            this.showMobileMenu();
            this.showButtons();
            this.buttons.forEach((button) => {
                button.setDisabled(false);
            });
        }
    }
}
